import { Badge } from "@/components/ui";
import { useOutreachRhythm } from "@/api/hooks/useOutreach";
import { formatDate } from "./format";

/**
 * The weekly rhythm quota board — GET /v2/outreach/rhythm's
 * {week_start, quotas:[{key,label,target,done}]}. done/target are
 * server-authoritative (mirrors ReviewCounterCard's review-count contract:
 * never recompute the target client-side); one card per quota, green once
 * done >= target.
 */
export function RhythmWidgets() {
  const { data, isLoading, error } = useOutreachRhythm();

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="rhythm-widgets">
        <p className="text-sm text-text-muted">Loading this week's rhythm...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-border bg-bg-card p-5" data-testid="rhythm-widgets">
        <p className="text-sm text-text-muted">Rhythm board unavailable right now.</p>
      </div>
    );
  }

  return (
    <div data-testid="rhythm-widgets">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">Weekly rhythm</h2>
        <span className="text-xs text-text-muted">Week of {formatDate(data.week_start)}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.quotas.map((quota) => (
          <RhythmQuotaCard key={quota.key} quota={quota} />
        ))}
      </div>
    </div>
  );
}

function RhythmQuotaCard({
  quota,
}: {
  quota: { key: string; label: string; target: number; done: number };
}) {
  const met = quota.done >= quota.target;
  const pct = quota.target > 0 ? Math.min(100, Math.round((quota.done / quota.target) * 100)) : 100;

  return (
    <div
      className="rounded-lg border border-border bg-bg-card p-4"
      data-testid={`rhythm-quota-${quota.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-text-secondary">{quota.label}</p>
        <Badge variant={met ? "success" : "secondary"} size="sm">
          {met ? "Done" : `${Math.max(0, quota.target - quota.done)} to go`}
        </Badge>
      </div>
      <p className="mt-1 font-display text-2xl font-bold text-text-primary">
        {quota.done} <span className="text-sm font-normal text-text-muted">of {quota.target}</span>
      </p>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-bg-muted"
        role="progressbar"
        aria-valuenow={quota.done}
        aria-valuemin={0}
        aria-valuemax={quota.target}
      >
        <div
          className={met ? "h-full rounded-full bg-success" : "h-full rounded-full bg-copper"}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
